/**
 * Viewport helpers for TOC location pips.
 *
 * KB articles scroll the window. Form editors (company notes, asset fields)
 * scroll `.notion-like-editor-content`. Pips should follow whichever of those
 * is the actual scrollport, using that container's top as the reading line.
 */

const EDITOR_CONTENT_SELECTOR = ".notion-like-editor-content"
const EDITOR_LAYOUT_SELECTOR = ".notion-like-editor-layout"
const FORM_EDITOR_MOUNT_SELECTOR = ".editor-component__mount"

const asElement = (node) => {
  if (!node) return null
  if (node.nodeType === 1) return node
  return node.parentElement
}

/**
 * Resolve a live heading element for a TOC item.
 *
 * TableOfContents stores `item.id` as `data-toc-id`, but UniqueID owns the
 * heading's HTML `id`. `document.getElementById(item.id)` therefore misses.
 * After a TOC click, ProseMirror may also replace the node, so `item.dom`
 * from the last TOC snapshot can be detached and `scrollIntoView` is a no-op.
 */
export const getTocHeadingElement = (item) => {
  if (!item || typeof document === "undefined") return null

  if (item.id) {
    const byTocId = document.querySelector(`[data-toc-id="${item.id}"]`)
    if (byTocId) return byTocId

    const byId = document.getElementById(item.id)
    if (byId) return byId
  }

  const live = asElement(item.dom)
  return live?.isConnected ? live : null
}

/**
 * Find the nearest overflowing ancestor of an element.
 * Falls back to `window` when no scrollable container is found.
 */
export const getScrollableAncestor = (element) => {
  let parent = asElement(element)?.parentElement

  while (parent) {
    const { overflowY } = getComputedStyle(parent)

    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent
    }

    parent = parent.parentElement
  }

  return window
}

/**
 * Prefer the editor content pane only inside form-field mounts, where that
 * pane is the dedicated scrollport. KB articles share the same class names
 * but scroll the window — treating them as a pane leaves already-clicked
 * headings stuck and ignores page scroll.
 */
export const getTocScrollRoot = (fromElement) => {
  if (typeof window === "undefined") return null

  const start = asElement(fromElement)
  const content =
    start?.closest?.(EDITOR_CONTENT_SELECTOR) ||
    start?.closest?.(EDITOR_LAYOUT_SELECTOR)?.querySelector(EDITOR_CONTENT_SELECTOR)

  if (content?.closest?.(FORM_EDITOR_MOUNT_SELECTOR)) {
    const { overflowY } = getComputedStyle(content)
    if (overflowY === "auto" || overflowY === "scroll") {
      return content
    }
  }

  if (start) return getScrollableAncestor(start)
  return window
}

export const getTocScrollListenerTarget = (scrollRoot) => {
  if (!scrollRoot || scrollRoot === window) return window
  return scrollRoot
}

const SCROLL_INTENT_KEYS = new Set([
  "PageUp",
  "PageDown",
  "Home",
  "End",
  "ArrowUp",
  "ArrowDown",
  " ",
])

/**
 * Keys that typically move the reading position. Used to clear a click-selected
 * TOC pip; `scroll` itself is ignored so programmatic scrollIntoView does not.
 *
 * Space on a control activates it rather than scrolling. Arrow/Page keys still
 * count when focus is in the sibling TOC, because that is where keyboard users
 * land after activating a heading.
 */
export const isTocUserScrollKey = (event) => {
  if (!event || event.altKey || event.ctrlKey || event.metaKey) return false
  if (!SCROLL_INTENT_KEYS.has(event.key)) return false

  if (event.key === " ") {
    const target = event.target
    if (
      target?.closest?.(
        "a, button, [role='button'], input, textarea, select, [contenteditable='true']",
      )
    ) {
      return false
    }
  }

  return true
}

/**
 * Y in viewport coordinates that counts as "the reading line".
 * Window scroll: sticky page chrome (`topOffset`). Inner pane: the pane top.
 */
export const getTocAnchorY = (scrollRoot, topOffset = 0) => {
  if (!scrollRoot || scrollRoot === window || scrollRoot === document.documentElement) {
    return topOffset + 1
  }

  return scrollRoot.getBoundingClientRect().top + 1
}

export const findViewportActiveHeadingId = (
  headingList,
  { topOffset = 0, scrollRoot = window } = {},
) => {
  if (!headingList?.length) return null

  const anchorY = getTocAnchorY(scrollRoot, topOffset)
  let activeId = headingList[0]?.id ?? null

  for (const item of headingList) {
    const element = getTocHeadingElement(item)
    if (!element) continue

    if (element.getBoundingClientRect().top <= anchorY) {
      activeId = item.id
    } else {
      break
    }
  }

  return activeId
}

const isWindowScrollRoot = (scrollRoot) =>
  !scrollRoot || scrollRoot === window || scrollRoot === document.documentElement

/**
 * Scroll a heading into view without moving ancestor scrollers.
 *
 * Native `scrollIntoView` walks every overflow container, so in form editors
 * it also pan-scrolls the page and hides the editor header. Do not set
 * inline `scroll-margin-top` on the heading: ProseMirror treats that DOM
 * mutation as a reason to replace the node, after which TOC `item.dom` is
 * stale and later clicks on the same heading do nothing.
 */
export const scrollHeadingIntoTocView = (
  element,
  { topOffset = 0, behavior = "smooth" } = {},
) => {
  const target = asElement(element)
  if (!target) return

  const scrollRoot = getTocScrollRoot(target)

  if (isWindowScrollRoot(scrollRoot)) {
    const nextTop = Math.max(0, window.scrollY + target.getBoundingClientRect().top - topOffset)
    window.scrollTo({ top: nextTop, behavior })
    return
  }

  const { scrollX, scrollY } = window
  const paneTop = scrollRoot.getBoundingClientRect().top
  const headingTop = target.getBoundingClientRect().top
  const nextTop = Math.max(0, scrollRoot.scrollTop + (headingTop - paneTop))

  if (typeof scrollRoot.scrollTo === "function") {
    scrollRoot.scrollTo({ top: nextTop, behavior })
  } else {
    scrollRoot.scrollTop = nextTop
  }

  const restoreWindow = () => {
    if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
      window.scrollTo(scrollX, scrollY)
    }
  }

  restoreWindow()
  if (typeof requestAnimationFrame !== "function") return

  requestAnimationFrame(() => {
    restoreWindow()
    requestAnimationFrame(restoreWindow)
  })
}
